import { createShapeNormalizer } from '@change-corgi/fcm/normalizers';

import { defineFcm } from '../shared/factory';

const mergeField = <T>(country: T, locale: T): T => country ?? locale;

function assertSixAmounts(value: unknown): readonly number[] {
	if (!Array.isArray(value)) throw new Error('amounts must be an array');
	if (value.length !== 6) throw new Error(`amounts must have exactly 6 values, got ${value.length}`);
	return value as readonly number[];
}

export const FCM_PROMOTE_AMOUNT_BUTTONS_SIMPLIFIED_EXPERIMENT = defineFcm(
	'promote_amount_buttons_simplified_experiment',
)
	.asJson(
		createShapeNormalizer({
			type: 'object',
			shape: {
				experimentName: { type: 'string', optional: true },
				countryCode: { type: 'string', optional: true },
				baseAmounts: { type: 'array', shape: { type: 'number' }, optional: true },
				preSelectedBaseAmount: { type: 'number', optional: true },
				experimentAmounts: { type: 'array', shape: { type: 'number' }, optional: true },
				preSelectedExperimentAmount: { type: 'number', optional: true },
				subheadingText: { type: 'string', optional: true },
				suggestedLabel: { type: 'string', optional: true },
				amountSelectorAriaLabel: { type: 'string', optional: true },
				impressionText: { type: 'string', optional: true },
				randomAmounts: {
					type: 'object',
					shape: {
						staticAmount_1: {
							type: 'object',
							shape: {
								amounts: { type: 'array', shape: { type: 'number' }, normalizer: assertSixAmounts },
							},
							optional: true,
						},
						staticAmount_2: {
							type: 'object',
							shape: {
								amounts: { type: 'array', shape: { type: 'number' }, normalizer: assertSixAmounts },
							},
							optional: true,
						},
					},
					optional: true,
				},
			},
		}),
	)
	.withDefault(undefined)
	.withMerger(({ countryValue, localeValue }) => {
		if (!countryValue && !localeValue) return undefined;
		const cv = countryValue ?? {};
		const lv = localeValue ?? {};
		return {
			experimentName: mergeField(cv.experimentName, lv.experimentName),
			countryCode: mergeField(cv.countryCode, lv.countryCode),
			baseAmounts: mergeField(cv.baseAmounts, lv.baseAmounts),
			preSelectedBaseAmount: mergeField(cv.preSelectedBaseAmount, lv.preSelectedBaseAmount),
			experimentAmounts: mergeField(cv.experimentAmounts, lv.experimentAmounts),
			preSelectedExperimentAmount: mergeField(cv.preSelectedExperimentAmount, lv.preSelectedExperimentAmount),
			subheadingText: mergeField(cv.subheadingText, lv.subheadingText),
			suggestedLabel: mergeField(cv.suggestedLabel, lv.suggestedLabel),
			amountSelectorAriaLabel: mergeField(cv.amountSelectorAriaLabel, lv.amountSelectorAriaLabel),
			impressionText: mergeField(cv.impressionText, lv.impressionText),
			randomAmounts: mergeField(cv.randomAmounts, lv.randomAmounts),
		};
	})
	.toConfig({
		owner: 'squad-koi',
		scope: 'corgi-front-end',
		context: 'promote',
		description: 'Controls the simplified layout experiment for amount buttons on the promote page',
		supportedGroups: { locales: true, countries: true },
		mergeLogic: 'each property: country value wins, else locale value',
		template: {
			experimentName: '',
			baseAmounts: [3, 10, 25, 50, 100, 250],
			preSelectedBaseAmount: 3,
			experimentAmounts: [5, 25, 50, 100, 250, 500],
			preSelectedExperimentAmount: undefined,
			countryCode: 'US',
			subheadingText: '',
			suggestedLabel: '',
			amountSelectorAriaLabel: '',
			impressionText: '',
			randomAmounts: undefined,
		},
		import: false,
	});
